import React, { useState, useEffect } from 'react';
import { Star, Check, X, Star as StarIcon } from 'lucide-react';

interface TestimonialType {
  id: number;
  name: string;
  role: string;
  rating: number;
  text: string;
  service: string;
  featured: boolean;
  approved?: boolean;
  flagged?: boolean;
  date?: string;
}

const TestimonialAdmin = () => {
  const [testimonials, setTestimonials] = useState<TestimonialType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch('/data/testimonials.json');
      const data = await response.json();
      setTestimonials(data.testimonials);
      setLoading(false);
    } catch (err) {
      setError('Failed to load testimonials');
      setLoading(false);
    }
  };

  const handleAction = async (testimonialId: number, action: string) => {
    try {
      const response = await fetch('/.netlify/functions/approve-testimonial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ testimonialId, action }),
      });

      if (!response.ok) {
        throw new Error('Failed to update testimonial');
      }

      // Refresh the testimonials list
      fetchTestimonials();
    } catch (err) {
      setError('Failed to update testimonial');
    }
  };

  if (loading) {
    return <div className="text-center p-4">Loading testimonials...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">Manage Testimonials</h2>
      
      <div className="grid gap-4">
        {testimonials.map((testimonial) => (
          <div 
            key={testimonial.id} 
            className={`p-4 rounded-lg ${
              testimonial.flagged ? 'bg-red-50' : 
              testimonial.approved ? 'bg-green-50' : 'bg-yellow-50'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-bold">{testimonial.name}</h3>
                <p className="text-sm text-gray-600">{testimonial.role}</p>
              </div>
              <div className="flex items-center">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <StarIcon key={i} className="text-yellow-400" size={16} />
                ))}
              </div>
            </div>
            
            <p className="mb-2">{testimonial.text}</p>
            
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{testimonial.service}</span>
                {testimonial.date && (
                  <span className="ml-2">
                    {new Date(testimonial.date).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              <div className="flex gap-2">
                {!testimonial.approved && (
                  <button
                    onClick={() => handleAction(testimonial.id, 'approve')}
                    className="p-2 text-green-600 hover:bg-green-100 rounded"
                    title="Approve testimonial"
                  >
                    <Check size={20} />
                  </button>
                )}
                
                {testimonial.approved && !testimonial.featured && (
                  <button
                    onClick={() => handleAction(testimonial.id, 'feature')}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                    title="Feature testimonial"
                  >
                    <Star size={20} />
                  </button>
                )}
                
                {testimonial.featured && (
                  <button
                    onClick={() => handleAction(testimonial.id, 'unfeature')}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                    title="Remove from featured"
                  >
                    <Star size={20} />
                  </button>
                )}
                
                <button
                  onClick={() => handleAction(testimonial.id, 'reject')}
                  className="p-2 text-red-600 hover:bg-red-100 rounded"
                  title="Reject testimonial"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestimonialAdmin; 